// Builds the localized tooltip/inline message for a live-collaboration entry
// point out of a `useCollabAvailability()` result. Kept as a pure function
// (no React, no i18n import) so the three entry points (top-right button,
// main-menu item, welcome-screen item) share one reason -> text mapping
// instead of repeating it, and so it is testable without rendering anything.

import type { CollabAvailability } from "./useCollabAvailability";

type Translate = (
  key: string,
  replacement?: { [key: string]: string | number },
) => string;

/**
 * Returns the message to show for the current availability state, or `null`
 * when the entry point needs no message at all (allowed and not loading).
 * Never renders a raw server code: every `reason` is routed through
 * `excalidraw-app/locales/` before it reaches this return value.
 */
export const buildCollabAvailabilityMessage = (
  availability: CollabAvailability,
  t: Translate,
): string | null => {
  if (availability.isLoading) {
    return t("collab.availability.checking");
  }
  if (availability.allowed) {
    return null;
  }

  const resetTime = availability.resetAt
    ? new Date(availability.resetAt).toLocaleTimeString()
    : "";
  const reasonMessage = t(
    `collab.availability.reason.${availability.reason}`,
    resetTime ? { resetTime } : undefined,
  );
  if (!availability.limits) {
    return reasonMessage;
  }
  const limitsMessage = t("collab.availability.limits", {
    maxPeers: availability.limits.maxPeers,
    minutes: availability.limits.sessionMaxMinutes,
  });
  return `${reasonMessage} ${limitsMessage}`;
};
